// React
import React, {useEffect, useState} from 'react';
import type {ComponentType} from 'react';

// Third party
import {Outlet, useParams} from 'react-router-dom';
import moment from 'moment';
import {observer} from 'mobx-react';
import 'emoji-mart/css/emoji-mart.css';

// WS
import {
  Api,
  BridgeEventSources,
  Events,
  Theme,
} from '@wellstone-solutions/common';

import {Box, Stack, Typography} from '@wellstone-solutions/web';
import {useStores} from 'hooks/useStores';
import {genders, identities} from 'utils/Forms';
import {NavTabs} from 'modules/navigation';
import {
  HEADER_CARD_TUTORIAL_IDS,
  HEADER_CARD_WIDE,
} from './MemberHeaderCard/containers';
import TutorialDiv from 'components/tutorial/tutorialdiv';
import {processData} from 'utils/Charts';
import EditMemberForm from 'components/forms/member/EditMemberForm';
import PfChatModule from 'components/chat/PfChatModule';
import {LoadingIndicator} from 'components/shared/LoadingIndicator';
import PfModal from 'components/modal/PfModal';
import {MemberHeaderCard} from './MemberHeaderCard';
import {EventEmitter} from 'utils/EventEmitter';
import {ResendInvitationEmail} from '../ResendInvitationEmail';
import {useFeatureFlag, LD_FLAGS} from 'modules/feature-flags';
import CommunitySwitch from 'components/forms/components/CommunitySwitch';
import {ENTITY_LEVEL, ENTITY_TYPES, AccessControl} from 'modules/rbac';
import {MemberDetailTabContext} from '../../contexts/MemberDetailTabContext';
import {EHRConnect} from 'modules/integration';
import {ErrorPage} from 'components/ErrorPage';
import {MemberSignalsCard} from 'modules/signals';

type PropsType = {};

export const MemberDetail: ComponentType<PropsType> = observer(
  ({memberNotifications}) => {
    const routeParams = useParams();
    const [state, setState] = useState(false);
    // Mirrors CommunitySwitch's optimistic toggle state so the header
    // tooltip copy can react to it; null until the user toggles.
    const [chatToggleValue, setChatToggleValue] = useState(null);

    const {
      eventStore,
      appUIStore,
      meStore,
      memberDetailStore,
      RBACStore,
      tutorialStore,
    } = useStores();
    const hasPeersDocumentation = useFeatureFlag(LD_FLAGS.PEERS_DOCUMENTATION);
    const emrManagedUsers = useFeatureFlag(LD_FLAGS.EMR_MANAGED_USERS);
    const signalBridgeEnabled = useFeatureFlag(LD_FLAGS.SIGNAL_BRIDGE);

    const hasGroupAccess = (groups) => {
      // this user has specific authorization for this group
      const isMine = groups
        ? groups.some((group) =>
            RBACStore.hasAccessToEntity(ENTITY_TYPES.group, group),
          )
        : false;
      // if the current user is an admin or a primary counselor
      return RBACStore.hasEntityLevelAccess(ENTITY_LEVEL.program) || isMine;
    };

    const {currentMember} = memberDetailStore;
    const hasPrimaryAccess = hasGroupAccess(memberDetailStore.primaryGroups);
    const canViewSessionDocTab = currentMember
      ? hasGroupAccess(
          currentMember.group_admissions.map((ga) => ga.group.id),
        ) && hasPeersDocumentation
      : false;

    const canAssignEHRIntegration =
      !!meStore.ehrIntegrationOption &&
      RBACStore.hasAccess(AccessControl.staff.canAssignEHRIntegrations);

    const setFocus = () => {
      setState({...state, isFocused: !state.isFocused});
    };

    useEffect(() => {
      eventStore.setEventSource(BridgeEventSources.MEMBER_DETAILS_PAGE);
      setChatToggleValue(null);
      loadUX();
    }, [routeParams.member]);

    useEffect(() => {
      getData();
    }, [state.aggregator]);

    useEffect(() => {
      if (
        state.selectionRange &&
        state.selectionRange.startDate !== state.selectionRange.endDate
      ) {
        EventEmitter.dispatch('closeModal');
        getData();
      }
    }, [state.selectionRange]);

    const loadUX = async () => {
      setState({...state, ready: false});

      eventStore.addEvent(Events.VIEWED_MEMBER_DETAIL, {
        member_id: routeParams.member, // this is the member id
      });

      let start = new Date();
      let end = new Date();
      start.setDate(start.getDate() - 6);
      end.setDate(end.getDate() + 1);

      const stateUpdates = {
        aggregator: 'date',
        selectionRange: {
          startDate: start,
          endDate: end,
          key: 'selection',
        },
        mainColumn: 6,
      };

      const org = meStore.me.membership.organization.id;

      try {
        const response = await memberDetailStore.getCurrentMember(
          org,
          routeParams.member,
        );

        if (!response.isSuccess) {
          setState({...state, ...stateUpdates, ready: true});
          return;
        }

        await memberDetailStore.syncCurrentMemberHabits();
        appUIStore.setBreadcrumbs(memberDetailStore.getBreadCrumbs());

        setState({
          ...state,
          ...stateUpdates,
          ready: true,
        });
      } catch (e) {
        setState({...state, ...stateUpdates, ready: true});
      }
    };

    const getMember = async () => {
      const org = meStore.me.membership.organization.id;

      try {
        const response = await Api.Instance.current().get(
          '/orgs/' + org + '/members/' + routeParams.member,
        );
        memberDetailStore.setCurrentMember(response.data);
        setState({...state, ready: true});
      } catch (e) {
        // TODO: (UX, Jer) if getting member fails?
        console.log('Failed to get member', e);
      } finally {
        setState({...state, ready: true});
      }
    };

    const getChatModule = () => {
      if (state.ready && state.mainColumn !== 12) {
        return (
          <TutorialDiv id="chat-wrapper">
            <PfChatModule
              user={memberDetailStore.currentMember.user.id}
              name={memberDetailStore.currentMember.name}
            />
          </TutorialDiv>
        );
      } else {
        return null;
      }
    };

    const handleSelectAggregator = async (sel) => {
      setState({...state, aggregator: sel});
    };

    const getData = async () => {
      if (
        !state.selectionRange?.startDate ||
        !state.selectionRange?.endDate ||
        !memberDetailStore.currentMember
      ) {
        return;
      }

      let start = moment(state.selectionRange.startDate).format('YYYY-MM-DD');
      let end = moment(state.selectionRange.endDate).format('YYYY-MM-DD');

      const params = {
        limit: 999999,
        range_start: start,
        range_end: end,
        member_id: routeParams.member,
        only_patient: 1,
      };
      const org = meStore.me.membership.organization.id;

      try {
        let response = await Api.Instance.current().get(
          '/orgs/' + org + '/events',
          {params},
        );
        const otherData = await processData(org, response, state);
        const habitData = memberDetailStore.getActivityData({
          aggregator: state.aggregator,
          eventsData: response.data.events,
          eventCategory: 'user_completed_habit',
          groupingFn: memberDetailStore.groupHabits,
          selectionRange: state.selectionRange,
        });

        const allData = {...otherData, habitData};

        setState({...state, ...allData});

        return allData;
      } catch (error) {
        console.log('Charts:error', error);
      }
    };

    if (!state.ready) {
      return <LoadingIndicator />;
    }

    if (!currentMember) {
      return <ErrorPage message="Member not found" isFullScreen={false} />;
    }

    const uiGender =
      genders.find(({value}) => value === currentMember.gender)?.name || 'N/A';
    const uiIdentifiesAs =
      identities.find(({value}) => value === currentMember.identifies_as)
        ?.name || 'N/A';

    const tabs = [
      {
        label: 'General',
        value: `general`,
        to: `/members/${routeParams.member}`,
      },
    ];

    if (RBACStore.hasAccess(AccessControl.members.canAddProgramEnrollment)) {
      tabs.push({
        label: 'Programs & Groups',
        value: `permissions`,
        to: `/members/${routeParams.member}/permissions`,
      });
    }

    tabs.push({
      label: 'Activity',
      value: `activity`,
      to: `/members/${routeParams.member}/activity`,
    });

    tabs.push({
      label: 'Healthy Habits',
      value: `habit-schedule`,
      to: `/members/${routeParams.member}/habit-schedule`,
    });

    if (canViewSessionDocTab) {
      tabs.push({
        label: 'Documentation',
        value: `session-docs`,
        to: `/members/${routeParams.member}/session-docs`,
      });
    }

    tabs.push({
      value: 'focus',
      label: state.isFocused ? 'Expand' : 'Focus',
      onClick: () => setFocus(true),
      style: {marginLeft: 'auto'},
    });

    return (
      <div>
        {!state.isFocused ? (
          <MemberHeaderCard
            isTutorialTarget={HEADER_CARD_TUTORIAL_IDS.some((id) =>
              tutorialStore?.highlightComponents?.includes(id),
            )}
            name={currentMember.name}
            userId={currentMember.user.id}
            globalId={currentMember.id}
            birthdate={currentMember.birthdate}
            email={currentMember.user.email}
            genderLabel={uiGender}
            identifiesAsLabel={uiIdentifiesAs}
            lastCheckIn={currentMember.last_check_in?.created}
            lastAppOpen={currentMember.last_opened_app?.created}
            editButton={
              RBACStore.hasAccess(AccessControl.members.editMember) &&
              !emrManagedUsers ? (
                <PfModal
                  button={{
                    outlined: true,
                    text: (
                      <>
                        <Box
                          component="span"
                          sx={{
                            display: 'none',
                            [HEADER_CARD_WIDE]: {display: 'inline'},
                          }}>
                          Edit Member
                        </Box>
                        <Box
                          component="span"
                          sx={{
                            display: 'inline',
                            [HEADER_CARD_WIDE]: {display: 'none'},
                          }}>
                          Edit
                        </Box>
                      </>
                    ),
                  }}
                  large
                  modal={{
                    closeOnBlur: true,
                    showClose: false,
                    closeOnEsc: true,
                  }}
                  onCloseModal={getMember}>
                  <Typography
                    variant="h6"
                    sx={{
                      marginBottom: 3,
                      color: Theme.colorPalette.onSurface,
                    }}>
                    Edit Member
                  </Typography>
                  <EditMemberForm member={currentMember} />
                </PfModal>
              ) : null
            }
            chatToggle={
              hasPrimaryAccess && currentMember.admissions.length > 0 ? (
                <CommunitySwitch
                  member={currentMember}
                  onChange={setChatToggleValue}
                />
              ) : null
            }
            chatEnabled={chatToggleValue ?? currentMember.has_community_access}
            chatFallback={
              <div
                style={{
                  ...styles.italic,
                  ...{
                    color: currentMember.has_community_access
                      ? Theme.colorPalette.green
                      : Theme.colorPalette.mediumDark,
                  },
                }}>
                {currentMember.has_community_access
                  ? 'Has peer-to-peer access'
                  : 'Does not have peer-to-peer access'}
              </div>
            }
            ehrConnect={
              canAssignEHRIntegration ? (
                <EHRConnect memberType={'member'} member={currentMember} />
              ) : null
            }
            resendInvite={
              RBACStore.hasAccess(AccessControl.members.sendReinvite) &&
              !currentMember.last_opened_app ? (
                <ResendInvitationEmail
                  id={currentMember.id}
                  lastInviteSent={currentMember.invite_sent?.created}
                  memberCreated={currentMember.created}
                  name={currentMember.name}
                  email={currentMember.user.email}
                  organizationId={meStore.organizationId}
                />
              ) : null
            }
          />
        ) : null}
        {signalBridgeEnabled && !state.isFocused && (
          <MemberSignalsCard
            memberId={currentMember.id}
            memberName={currentMember.name}
          />
        )}
        <Stack
          mb={4}
          sx={{borderBottom: 1, borderColor: 'divider', width: '100%'}}>
          <NavTabs tabs={tabs} />
        </Stack>
        <MemberDetailTabContext.Provider
          value={{
            getChatModule,
            member: memberDetailStore.currentMember,
            searchBoxTally: meStore.searchBoxTally,
            getData,
            state,
            setState,
            memberNotifications,
            onSelectAggregator: handleSelectAggregator,
          }}>
          <Outlet />
        </MemberDetailTabContext.Provider>
      </div>
    );
  },
);

const styles = {
  stackedLevelItem: {
    marginBottom: 5,
  },
  stackedLevelItemBig: {
    marginBottom: 8,
  },
  basicInfo: {
    display: 'block',
    marginTop: 10,
    color: Theme.colorPalette.onSurface,
  },
  memberName: {
    marginBottom: 10,
    color: Theme.colorPalette.onSurface,
  },
  infoBlock: {
    marginBottom: 20,
  },
  inlineBlock: {display: 'inline-block'},
  groupHeader: {
    fontSize: 16,
    fontWeight: 700,
    color: Theme.colorPalette.onSurface,
    marginTop: 10,
    marginBottom: 10,
  },
  adminHeader: {
    textAlign: 'right',
    backgroundColor: Theme.colorPalette.primary,
    padding: 10,
    borderRadius: 5,
  },
  adminHeaderText: {
    color: 'white',
  },
  chatHeaders: {
    marginLeft: 5,
    marginRight: 5,
  },
  patientHeader: {
    backgroundColor: Theme.colorPalette.outline,
    padding: 10,
    borderRadius: 5,
  },
  patientHeaderText: {
    color: Theme.colorPalette.primary,
  },
  viewBy: {
    fontWeight: 'bold',
    color: Theme.colorPalette.onSurface,
  },
  gradient: {
    position: 'absolute',
    height: '100%',
    width: '100%',
    backgroundImage:
      'linear-gradient(0deg, white, rgba(255,255,255,0), rgba(255,255,255,0))',
    zIndex: 9,
    top: '-0.5rem',
    left: '0rem',
    pointerEvents: 'none',
  },
  boxrelative: {
    position: 'relative',
    backgrouncColor: '#FFF',
  },

  stackedLeftInner: {
    marginBottom: 10,
  },
  stackedRightInner: {
    marginBottom: 10,
    marginTop: 20,
    flexGrow: 0,
  },
  tabSpacer: {
    flex: 2,
  },
  streamContainer: {
    position: 'relative',
  },
  relative: {
    position: 'relative',
    height: 40,
  },
  button: {
    height: 40,
    right: 0,
  },
  program: {
    fontWeight: '600',
    padding: 10,
    fontSize: 16,
    margin: 0,
  },
  block: {
    display: 'block',
  },
  m0: {
    margin: 0,
  },
  italic: {
    fontStyle: 'italic',
  },
  notActive: {
    color: Theme.colorPalette.mediumDark,
    fontStyle: 'italic',
    fontSize: 15,
  },
};
